import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import Button from "../../common/Button";
import Select from "../../common/SelectInput";
import success from "../../../assets/agent/success.svg";
import fail from "../../../assets/agent/fail.svg";
import { useContactContext } from "../../../contexts/ContactContext";

function CreateContactModal({ open, onClose }) {
  const { refreshContacts } = useContactContext();
  const modalRef = useRef(null);
  const { t } = useTranslation();
  const clinentId = sessionStorage.getItem("clinentId");

  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const channels = [
    { value: "Email", label: t("email") },
    { value: "WhatsApp", label: t("whatsapp") },
    { value: "Call", label: t("call") },
  ];
  const industries = [
    {
      value: "Agriculture, Fishing & Forestry",
      label: t("agent.contact.industry.agricultureFishingForestry"),
    },
    {
      value: "Mining & quarrying",
      label: t("agent.contact.industry.miningQuarrying"),
    },
    {
      value: "Manufacturing",
      label: t("agent.contact.industry.manufacturing"),
    },
    {
      value: "Electricity, gas & energy supply",
      label: t("agent.contact.industry.electricityGasEnergy"),
    },
    {
      value: "Water supply & waste management",
      label: t("agent.contact.industry.waterWasteManagement"),
    },
    { value: "Construction", label: t("agent.contact.industry.construction") },
    {
      value: "Wholesale & retail trade",
      label: t("agent.contact.industry.wholesaleRetailTrade"),
    },
    {
      value: "Transportation & storage",
      label: t("agent.contact.industry.transportationStorage"),
    },
    {
      value: "Accommodation & food services",
      label: t("agent.contact.industry.accommodationFoodServices"),
    },
    {
      value: "Information & communication",
      label: t("agent.contact.industry.informationCommunication"),
    },
    {
      value: "Financial & insurance activities",
      label: t("agent.contact.industry.financialInsuranceActivities"),
    },
    {
      value: "Real Estate Activities",
      label: t("agent.contact.industry.realEstateActivities"),
    },
    {
      value: "Professional, scientific & technical services",
      label: t("agent.contact.industry.professionalScientificTechnical"),
    },
    {
      value: "Administrative & support services",
      label: t("agent.contact.industry.administrativeSupportServices"),
    },
    {
      value: "Public administration & defense",
      label: t("agent.contact.industry.publicAdministrationDefense"),
    },
    { value: "Education", label: t("agent.contact.industry.education") },
    {
      value: "Human health & social work",
      label: t("agent.contact.industry.humanHealthSocialWork"),
    },
    {
      value: "Arts, entertainment & recreation",
      label: t("agent.contact.industry.artsEntertainmentRecreation"),
    },
    {
      value: "Other Service Activities",
      label: t("agent.contact.industry.otherServiceActivities"),
    },
    {
      value: "Activities of households as employers",
      label: t("agent.contact.industry.householdActivities"),
    },
    {
      value: "Extraterritorial Organizations",
      label: t("agent.contact.industry.extraterritorialOrganizations"),
    },
  ];

  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    role: "",
    industry: "",
    phone: "",
    cellphone: "",
    mail: "",
    city: "",
    webpage: "",
    channel: "",
  });

  const handleBackdropClick = (e) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
      setNewContact("");
    }
  };
  const handleClose = (e) => {
    onClose();
    setNewContact("");
  };

  const mapFormDataToApi = (formData) => {
    return {
      AltEmail: "",
      City: formData.city || "",
      ClientId: clinentId,
      Company: formData.companyName || "",
      Country: formData.country || "",
      Email: formData.mail || "",
      FirstName: formData.firstName || "",
      Industry: formData.industry || "",
      InterestLevel: 0,
      IsInterested: null,
      LastName: formData.lastName || "",
      Mobile: formData.cellphone || "",
      Phone: formData.phone || "",
      PreferredChannel: formData.channel || "",
      Role: formData.role || "",
      Source: null,
      Status: 1,
      Website: formData.webpage || "",
    };
  };

  const handleSaveNewContact = async () => {
    try {
      const accessToken = await getValidToken();
      const apiData = mapFormDataToApi(newContact);
      const response = await axios.post(
        `${process.env.REACT_APP_APIBASEURL}/api/contacts`,
        apiData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setNewContact("");
        refreshContacts();
        onClose();
        setSuccessModal(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || t("error.generic"));
      setErrorModal(true);
    }
  };

  // Handle new contact input changes
  const handleNewContactChange = (field, value) => {
    setNewContact((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (successModal) {
      const timer = setTimeout(() => {
        setSuccessModal(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [successModal]);

  if (!open && !successModal) return null;

  const handleModal = () => {
    setErrorModal(false);
  };

  return (
    <>
      {open && (
        <div
          ref={modalRef}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4"
        >
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[580px] px-6 py-5 relative flex flex-col items-center">
            <span
              className="absolute top-5 right-5 text-sm font-bold cursor-pointer"
              onClick={handleClose}
            >
              X
            </span>
            <p className="mb-1 lg:text-xl font-bold">
              {t("agent.addMod.head")}
            </p>
            <p className="mb-3 text-sm lg:w-[370px] w-[350px] text-center">
              {t("agent.addMod.desc")}
            </p>

            <div className="relative flex items-center mb-5">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[250px] lg:w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>

            <div className="gap-2 grid grid-cols-2">
              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.name")}
                </label>
                <input
                  type="text"
                  value={newContact.firstName}
                  onChange={(e) =>
                    handleNewContactChange("firstName", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.lastName")}
                </label>
                <input
                  type="text"
                  value={newContact.lastName}
                  onChange={(e) =>
                    handleNewContactChange("lastName", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("dashboard.table.company")}
                </label>
                <input
                  type="text"
                  value={newContact.companyName}
                  onChange={(e) =>
                    handleNewContactChange("companyName", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("admin.role")}
                </label>
                <input
                  type="text"
                  value={newContact.role}
                  onChange={(e) =>
                    handleNewContactChange("role", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>
              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">Sector</label>
                <Select
                  name="industry"
                  value={newContact.industry}
                  onChange={(e) =>
                    handleNewContactChange("industry", e.target.value)
                  }
                  options={industries}
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("admin.phone")}
                </label>
                <input
                  type="text"
                  value={newContact.phone}
                  onChange={(e) =>
                    handleNewContactChange("phone", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">Ext</label>
                <input
                  type="text"
                  value={newContact.ext}
                  onChange={(e) =>
                    handleNewContactChange("ext", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.mobile")}
                </label>
                <input
                  type="text"
                  value={newContact.cellphone}
                  onChange={(e) =>
                    handleNewContactChange("cellphone", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("email")}
                </label>
                <input
                  type="email"
                  value={newContact.mail}
                  onChange={(e) =>
                    handleNewContactChange("mail", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.country")}
                </label>
                <input
                  type="text"
                  value={newContact.country}
                  onChange={(e) =>
                    handleNewContactChange("country", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.city")}
                </label>
                <input
                  type="text"
                  value={newContact.city}
                  onChange={(e) =>
                    handleNewContactChange("city", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1">
                  {t("confirm.table.website")}
                </label>
                <input
                  type="text"
                  value={newContact.webpage}
                  onChange={(e) =>
                    handleNewContactChange("webpage", e.target.value)
                  }
                  className="w-full lg:w-[200px] text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                  required
                />
              </div>

              <div className="mb-1">
                <label className="block text-sm font-medium mb-1 mr-12">
                  {t("agent.Channel")}
                </label>
                <Select
                  name="channel"
                  value={newContact.channel}
                  onChange={(e) =>
                    handleNewContactChange("channel", e.target.value)
                  }
                  options={channels}
                  required
                />
              </div>
            </div>

            <div className="relative flex items-center mb-1">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[250px] lg:w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>

            <div className="mt-4">
              <Button
                variant="primary"
                onClick={handleSaveNewContact}
                className="w-full lg:w-[500px]"
              >
                {t("agent.addMod.addBtn")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("agent.addMod.confirm.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl"> {t("agent.addMod.confirm.desc")}</p>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("agent.addMod.errorHead")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-4 text-xl font-bold text-red-600">
              {errorMessage}
            </p>
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateContactModal;
